import {
  GetEssDeviceMonitorStats,
  GetEssEnergyAnalysisStats,
  GetEssEnergyConversionStats,
  GetEssEnergyGrowthTrend,
  GetEssStationGeoStats,
  GetEssStationRank,
} from '@/api/evses'
import { GetEssDeviceMonitorStatsReply, EssEnergyChart } from '@/types/common/station'
import { onMounted, reactive, ref } from 'vue'
import type {
  DeviceMonitorStatsChartData,
  EssEnergyGrowthTrendChartData,
  GetEssEnergyConversionStatsReply,
  GetEssStationGeoStatsReply,
  Tab,
} from '@/views/DataV/Photovoltaic/types'
import { EssRankFilter, EssStationRank, StatisticsTimeGrain } from '@/types/types'

export const usePhotovoltaicData = () => {
  const deviceMonitorStats = ref<Partial<GetEssDeviceMonitorStatsReply>>({})
  const deviceMonitorStatsChartData = ref<DeviceMonitorStatsChartData[]>([
    { value: 0, name: '运营设备' },
    { value: 0, name: '待机设备' },
    { value: 0, name: '故障设备' },
    { value: 0, name: '离线设备' },
  ])

  const fetchDeviceMonitorStats = () => {
    GetEssDeviceMonitorStats()
      .then((res: GetEssDeviceMonitorStatsReply) => {
        deviceMonitorStats.value = res
        console.log('设备监控统计数据', res)
        deviceMonitorStatsChartData.value = [
          { value: res.operatingCount, name: '运营设备' },
          { value: res.standbyCount, name: '待机设备' },
          { value: res.breakdownCount, name: '故障设备' },
          { value: res.offlineCount, name: '离线设备' },
        ]
      })
      .catch((err: any) => {
        console.log('获取设备监控统计数据失败', err)
      })
  }

  const essEnergyGrowthTrend = ref<EssEnergyChart[]>([])
  const essEnergyGrowthTrendChartData = ref<EssEnergyGrowthTrendChartData>({})

  const fetchEssEnergyGrowthTrend = () => {
    GetEssEnergyGrowthTrend({
      grain: 10,
    }).then((res: { curYear: EssEnergyChart[] }) => {
      essEnergyGrowthTrendChartData.value = {
        xAxisData: res.curYear.map((item) => item.time),
        seriesData: [
          {
            name: '充电量(kWh)',
            data: res.curYear.map((item) => item.chargeEnergy),
          },
          {
            name: '放电量(kWh)',
            data: res.curYear.map((item) => item.dischargeEnergy),
          },
          {
            name: '光伏PV发电量(kWh)',
            data: res.curYear.map((item) => item.chargeEnergyPV),
          },
        ],
      }
    })
  }

  const stationRankList = ref<Tab[]>([])
  const fetchEssStationRank = async () => {
    const results = await Promise.all(
      [
        {
          label: '储能充电',
          value: EssRankFilter.ERF_ChargeEnergy,
        },
        {
          label: '储能放电',
          value: EssRankFilter.ERF_DischargeEnergy,
        },
        {
          label: '光伏发电',
          value: EssRankFilter.ERF_GenEnergy,
        },
      ].map(async (type) => {
        const { list } = await GetEssStationRank({
          pageReq: {
            page: 1,
            limit: 4,
          },
          rankFilter: type.value,
        })
        return {
          label: type.label,
          list: list.map((item: EssStationRank) => {
            return {
              name: item.stationName,
              value:
                type.value === EssRankFilter.ERF_ChargeEnergy
                  ? item.chargeEnergy
                  : type.value === EssRankFilter.ERF_DischargeEnergy
                  ? item.dischargeEnergy
                  : type.value === EssRankFilter.ERF_GenEnergy
                  ? item.genEnergy
                  : 0,
            }
          }),
        }
      }),
    )
    stationRankList.value = results
  }

  const stationGeoStats = ref<Partial<GetEssStationGeoStatsReply>>({})
  const fetchEssStationGeoStats = () => {
    GetEssStationGeoStats().then((res: GetEssStationGeoStatsReply) => {
      stationGeoStats.value = res
    })
  }

  const essEnergyConversionStats = ref<Partial<GetEssEnergyConversionStatsReply>>({})
  const fetchEssEnergyConversionStats = () => {
    GetEssEnergyConversionStats().then((res: GetEssEnergyConversionStatsReply) => {
      essEnergyConversionStats.value = res
    })
  }

  const essEnergyAnalysisStatsChartData = ref<EssEnergyGrowthTrendChartData>({})

  const fetchEssEnergyAnalysisStats = () => {
    GetEssEnergyAnalysisStats({
      grain: StatisticsTimeGrain.STD_DAY,
    }).then((res: { list: EssEnergyChart[] }) => {
      essEnergyAnalysisStatsChartData.value = {
        xAxisData: res.list.map((item) => item.time),
        seriesData: [
          {
            name: '充电量',
            data: res.list.map((item) => item.chargeEnergy),
          },
          {
            name: '放电量',
            data: res.list.map((item) => item.dischargeEnergy),
          },
          {
            name: '光伏PV发电量',
            data: res.list.map((item) => item.chargeEnergyPV),
          },
          {
            name: '储能柜馈网电量',
            data: res.list.map((item) => item.feedbackGridEnergy),
          },
          {
            name: '光伏馈网电量',
            data: res.list.map((item) => item.feedbackGridEnergyPV),
          },
        ],
      }
    })
  }

  const essEnergyAnalysisStatsMonthChartData = ref<EssEnergyGrowthTrendChartData>({})

  const fetchEssEnergyAnalysisStatsByMonth = () => {
    GetEssEnergyAnalysisStats({
      grain: StatisticsTimeGrain.STD_MONTH,
    }).then((res: { list: EssEnergyChart[] }) => {
      essEnergyAnalysisStatsMonthChartData.value = {
        xAxisData: res.list.map((item) => item.time),
        seriesData: [
          {
            name: '充电量',
            data: res.list.map((item) => item.chargeEnergy),
          },
          {
            name: '放电量',
            data: res.list.map((item) => item.dischargeEnergy),
          },
          // {
          //   name: '负载功率',
          //   data: res.list.map((item) => item.loadPower),
          // },
          // {
          //   name: '光伏PV发电量',
          //   data: res.list.map((item) => item.chargeEnergyPV),
          // },
          // {
          //   name: '储能柜馈网电量',
          //   data: res.list.map((item) => item.feedbackGridEnergy),
          // },
          // {
          //   name: '光伏馈网电量',
          //   data: res.list.map((item) => item.feedbackGridEnergyPV),
          // },
        ],
      }
    })
  }

  onMounted(() => {
    fetchDeviceMonitorStats()
    fetchEssEnergyGrowthTrend()
    fetchEssStationRank()
    fetchEssStationGeoStats()
    fetchEssEnergyConversionStats()
    fetchEssEnergyAnalysisStats()
    fetchEssEnergyAnalysisStatsByMonth()
  })

  return {
    deviceMonitorStatsChartData,
    deviceMonitorStats,
    essEnergyGrowthTrend,
    essEnergyGrowthTrendChartData,
    stationRankList,
    stationGeoStats,
    essEnergyConversionStats,
    essEnergyAnalysisStatsChartData,
    essEnergyAnalysisStatsMonthChartData,
    fetchDeviceMonitorStats,
    fetchEssStationRank,
  }
}
